/**
 * BarcodeListener - web implementation.
 *
 * Same props and same contract as barcodeListerner.tsx (Android/iOS), so
 * App.tsx imports it without knowing which platform it is on.
 *
 * The native version keeps a hidden TextInput focused to catch wedge input.
 * That trick is unnecessary - and fragile - in a browser: a desktop scanner is
 * a keyboard, so we listen on the document instead. This means the operator can
 * scan at any time without a particular field being focused, which is how a
 * counter-top POS is expected to behave.
 *
 * Scans are handed on only while the sell screen is open. The scanned code is
 * parked in Redux for the sell screen, which adds it to the cart the next time
 * it gains focus - so a barcode scanned into, say, a product's UPC field on the
 * edit screen used to turn up in the cart later, unasked. Elsewhere the scan
 * simply types into whatever field has focus, as any keyboard input would.
 */

import {useEffect, useRef} from 'react';

import {startKeyboardWedgeScanner} from '../../platform/scanner/scanner';

/** Web paths of the sell screen (src/platform/navigation/webRoutes.ts). */
const SELL_SCREEN_PATH = /^\/(orders\/)?sell\/?$/;

export default function BarcodeListener({
  onScan,
  enabled = true,
}: {
  onScan: (barcode: string) => void;
  enabled?: boolean;
}) {
  // App.tsx passes a new function every render; keep the latest without
  // tearing the listener down (and dropping a half-read scan) each time.
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    return startKeyboardWedgeScanner({
      onScan: barcode => {
        if (SELL_SCREEN_PATH.test(window.location.pathname)) {
          onScanRef.current(barcode);
        }
      },
    });
  }, [enabled]);

  // Nothing to render - the listener is global.
  return null;
}
